import { normalizeDateRangeRows, validateDateRanges } from '../utils/courseSchedule'
import type { CourseDateRangeRow } from '../utils/courseSchedule'

/**
 * Course validation for the admin app.
 *
 * Character limits apply only to full description and short description (aligned
 * with backend Joi + Mongoose). Other text fields are not length-capped client-side.
 */

export const PATTERNS = {
  time24: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
} as const

export const LIMITS = {
  courseDescription: { min: 10, max: 700 },
  courseShortDescription: { max: 200 },
  startDay: { min: 1, max: 31 },
  maxImages: 5,
  maxVideos: 2,
} as const

export type ValidationErrors = Record<string, string>

export interface CourseFormData {
  name: string
  description: string
  shortDescription?: string
  duration?: number
  startDay?: number
  dateRangeRows?: CourseDateRangeRow[]
  schedule?: { timeSlots?: Array<{ label?: string; startTime?: string; endTime?: string }> }
  inclusions?: string[]
  requirements?: string[]
  whoIsThisFor?: string[]
  courseHighlights?: string[]
  whatYouWillGain?: string[]
  curriculum?: Array<{ title: string; items: string[] }>
  media?: { images?: string[]; videos?: string[] }
  instructor?: { name?: string; bio?: string; certifications?: string[] }
  accommodation?: { mealPlan?: { description?: string } }
}

export function validateCourse(data: CourseFormData): ValidationErrors {
  const err: ValidationErrors = {}

  const n = (data.name ?? '').trim()
  if (!n) err.name = 'Course name is required'

  const d = (data.description ?? '').trim()
  if (!d) err.description = 'Description is required'
  else if (d.length < LIMITS.courseDescription.min)
    err.description = `Description must be at least ${LIMITS.courseDescription.min} characters`
  else if (d.length > LIMITS.courseDescription.max)
    err.description = `Description cannot exceed ${LIMITS.courseDescription.max} characters`

  if (data.shortDescription?.trim() && data.shortDescription.trim().length > LIMITS.courseShortDescription.max)
    err.shortDescription = `Short description cannot exceed ${LIMITS.courseShortDescription.max} characters`

  if (data.duration != null && (data.duration < 1 || isNaN(data.duration)))
    err.duration = 'Duration must be at least 1 day'

  if (data.startDay != null && (data.startDay < LIMITS.startDay.min || data.startDay > LIMITS.startDay.max))
    err.startDay = `Start day must be between ${LIMITS.startDay.min} and ${LIMITS.startDay.max}`

  const scheduleErr = validateDateRanges(normalizeDateRangeRows(data.dateRangeRows || []))
  if (scheduleErr) err.dateRanges = scheduleErr

  const slots = data.schedule?.timeSlots || []
  if (slots.length === 0) err.schedule = 'At least one time slot is required'
  else {
    const slotWithoutLabel = slots.find((ts) => !(ts?.label || '').trim())
    if (slotWithoutLabel) err.schedule = 'Label is required for each time slot'
    for (let i = 0; i < slots.length; i++) {
      const ts = slots[i]
      if (ts?.startTime && !PATTERNS.time24.test(ts.startTime))
        err[`schedule.timeSlots[${i}].startTime`] = 'Start time must be in HH:MM format (24h)'
      if (ts?.endTime && !PATTERNS.time24.test(ts.endTime))
        err[`schedule.timeSlots[${i}].endTime`] = 'End time must be in HH:MM format (24h)'
    }
  }

  const images = data.media?.images || []
  const videos = data.media?.videos || []
  if (images.length > LIMITS.maxImages) err.media = `Maximum ${LIMITS.maxImages} images allowed`
  if (videos.length > LIMITS.maxVideos) err.media = err.media || `Maximum ${LIMITS.maxVideos} videos allowed`

  return err
}
